import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { AccountTypes, GiftingDesignationTypes } from '@shared/models';
import { WindowRef } from '@shared/services/window-ref/window-ref.service';
import { getConfigClientFromRTE } from '@src/app/core/config/config-client';
import {
  getInternalTransferTriage,
  InternalTransferState,
} from '@store/reducers/internal-transfer/internal-transfer.reducer';
import { selectDivorceToggle, selectItMultiReceivingToggle } from '@store/selectors';
import { map, withLatestFrom } from 'rxjs';

import { External, InternalTransferSummaryContinue } from '../../actions';
import { State } from '../../reducers/index';
import { selectInternalTransferState } from '../../selectors/internal-transfer/internal-transfer.selectors';

@Injectable()
export class InternalTransferSummaryEffects {
  constructor(
    private actions$: Actions,
    private globalStore$: Store<State>,
    private windowRef: WindowRef,
  ) {}

  internalTransferSummaryContinue$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InternalTransferSummaryContinue),
      withLatestFrom(
        this.globalStore$.pipe(select(selectInternalTransferState)),
        this.globalStore$.pipe(select(selectDivorceToggle)),
        this.globalStore$.pipe(select(selectItMultiReceivingToggle)),
      ),
      map(([, triageState, divorceToggle, multiReceivingToggle]) => {
        const url = this.buildUrl(triageState, divorceToggle, multiReceivingToggle);
        return External({ url: url });
      }),
    ),
  );

  buildUrl(
    triageState: InternalTransferState,
    divorceToggle: boolean,
    multiReceivingToggle: boolean,
  ): string {
    const config = getConfigClientFromRTE();
    const _triageState = getInternalTransferTriage(triageState);
    const accountFromType = _triageState.transferFrom;
    const accountToType = _triageState.transferTo;
    const isOwner = _triageState.isOwner;
    const exceptionsApply = _triageState.exceptionsApply;
    const isDivorceTransfer = _triageState?.isDivorceTransfer && divorceToggle;
    const intelledoxURL = config.pretty_ecs_domain + config.internal_transfer_intelledox_endpoint;
    const possibleInheritance = triageState.triage.transferTo === AccountTypes.ESTATE;
    const possibleGift = triageState.triage.isGift && divorceToggle;
    const divorceFlow =
      triageState.triage.transferFrom === AccountTypes.IRA &&
      isDivorceTransfer &&
      divorceToggle &&
      triageState.triage.transferTo === AccountTypes.IRA;
    const estateTransfer = triageState.triage.transferFrom === AccountTypes.ESTATE;
    const isMinorGift = !estateTransfer && triageState.triage.transferTo === AccountTypes.UGMA_UTMA;
    const isMultiReceivingEligible = multiReceivingToggle && accountFromType === AccountTypes.INDV;

    const validAccountTypes = (type: AccountTypes): boolean => {
      const validTypes = [
        AccountTypes.INDV,
        AccountTypes.TRUST,
        AccountTypes.JOINT,
        AccountTypes.ESTATE,
        AccountTypes.UGMA_UTMA,
        AccountTypes.IRA,
        AccountTypes.ORG,
      ];
      return validTypes.includes(type);
    };
    if (
      !validAccountTypes(accountFromType) ||
      (!validAccountTypes(accountToType) && !isDivorceTransfer)
    ) {
      return intelledoxURL;
    }
    const baseHref = this.windowRef.nativeWindow.location.href
      .split('?')[0]
      .slice(0, -'/internal-transfer/summary'.length);
    if (divorceFlow) {
      const divorceURL = baseHref + config.divorce_document_upload_endpoint;
      return divorceURL;
    } else if (possibleInheritance) {
      const inheritanceURL = baseHref + config.inheritance_overview_endpoint;
      return inheritanceURL;
    } else if (
      (!isOwner && !exceptionsApply && !estateTransfer && !divorceToggle) ||
      isMinorGift ||
      possibleGift
    ) {
      const accountFrom: AccountTypes = triageState.triage.transferFrom;
      const giftingURL =
        baseHref +
        config.gifting_triage_endpoint +
        '/' +
        GiftingDesignationTypes.VANGUARD.toLowerCase() +
        '/' +
        accountFrom.toLowerCase();
      return giftingURL;
    }

    const internalTransferEndpoint = isMultiReceivingEligible
      ? config.internal_transfer_from_to_endpoint
      : config.internal_transfer_details_endpoint;
    const cooURL = baseHref + internalTransferEndpoint;
    return cooURL;
  }
}

export const effects: any[] = [InternalTransferSummaryEffects];
